import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faBellSlash } from "@fortawesome/free-solid-svg-icons";
import api from "../lib/api.js";
import { useAuthStore } from "../lib/store.js";

const profileSchema = z.object({
  first_name:   z.string().optional(),
  last_name:    z.string().optional(),
  phone_number: z.string().optional(),
  time_zone:    z.string(),
});

export default function Settings() {
  const user    = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const qc      = useQueryClient();

  const toggleNotifMutation = useMutation({
    mutationFn: () =>
      api.patch(`users/${user.id}`, { json: { user: { notifications_enabled: !user.notifications_enabled } } }).json(),
    onSuccess: (updated) => { setUser(updated); qc.invalidateQueries(["me"]); },
  });

  const { register, handleSubmit, formState: { isSubmitting } } = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name:   user?.first_name || "",
      last_name:    user?.last_name  || "",
      phone_number: user?.phone_number || "",
      time_zone:    user?.time_zone  || "UTC",
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data) => api.patch(`users/${user.id}`, { json: { user: data } }).json(),
    onSuccess: (updated) => setUser(updated),
  });

  return (
    <div className="p-6 space-y-6 max-w-xl">
      <div>
        <h1 className="text-xl font-semibold data" style={{ color: "var(--color-white)" }}>Settings</h1>
        <p className="data text-xs mt-1" style={{ color: "var(--color-muted)" }}>Your profile</p>
      </div>

      {/* Global notification toggle */}
      <div className="card p-5 flex items-center justify-between">
        <div>
          <div className="data font-semibold flex items-center gap-2" style={{ color: "var(--color-white)" }}>
            <FontAwesomeIcon icon={user.notifications_enabled ? faBell : faBellSlash} style={{ color: user.notifications_enabled ? "var(--color-blue)" : "var(--color-muted)", fontSize: 13 }} />
            SMS Alerts
          </div>
          <div className="data text-xs mt-0.5" style={{ color: "var(--color-muted)" }}>
            Send an SMS to your phone number when a rave is detected on an opted-in device
          </div>
        </div>
        <button
          onClick={() => toggleNotifMutation.mutate()}
          disabled={toggleNotifMutation.isPending}
          style={{
            background: user.notifications_enabled ? "rgba(68,136,255,0.12)" : "transparent",
            border: `1px solid ${user.notifications_enabled ? "rgba(68,136,255,0.4)" : "var(--color-border)"}`,
            borderRadius: 6, padding: "8px 16px", cursor: "pointer",
            color: user.notifications_enabled ? "var(--color-blue)" : "var(--color-muted)",
            fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.06em",
            transition: "all 0.2s",
          }}
        >
          {user.notifications_enabled ? "ON" : "OFF"}
        </button>
      </div>

      <div className="card p-6">
        <form onSubmit={handleSubmit((d) => updateMutation.mutate(d))} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="First Name"    name="first_name"   register={register} />
            <Field label="Last Name"     name="last_name"    register={register} />
          </div>
          <Field label="Phone (E.164)"   name="phone_number" register={register} placeholder="+41..." />
          <Field label="Timezone"        name="time_zone"    register={register} placeholder="Europe/Zurich" />

          <div className="flex items-center gap-4 pt-1">
            <button type="submit" disabled={isSubmitting} className="btn btn-cyan">
              {isSubmitting ? "Saving…" : "Save"}
            </button>
            {updateMutation.isSuccess && (
              <span className="data text-xs" style={{ color: "var(--color-blue)" }}>Saved</span>
            )}
            {updateMutation.isError && (
              <span className="data text-xs" style={{ color: "var(--color-red)" }}>Error saving</span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, name, register, placeholder }) {
  return (
    <div>
      <label className="block mb-1 data text-xs" style={{ color: "var(--color-muted)", letterSpacing: "0.06em" }}>
        {label}
      </label>
      <input {...register(name)} className="input" placeholder={placeholder} />
    </div>
  );
}
